import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Table,
  Avatar,
  TableRow,
  TableBody,
  TableCell,
  Typography,
  TableContainer,
  TablePagination,
} from "@mui/material";
import SearchNotFound from "src/components/NotFound/SearchNotFound";
import Label from "src/components/Label";
import { UserListHead, UserListToolbar } from "src/components/_dashboard/user";
import { CircularProgress } from "@mui/material";
import { useSnackbar } from "notistack";
import { makeStyles } from "@mui/styles";
import CustomPopoverSection from "src/components/MenuOption/CustomPopoverSection";
import CustomConfirmation from "src/components/ModalPopover/Confirmation";
import { useContentSetting } from "src/Hooks/ContentSettingState";
import { member_login_api } from "src/DAL/member/member";
import { coachPortalUrl, s3baseUrl } from "src/config/config";
import { coachListing, DeleteCoachApi } from "src/DAL/coach/coach";
import { dd_date_format } from "src/utils/constant";

const TABLE_HEAD = [
  { id: "action", label: "Action", alignRight: false },
  { id: "number", label: "#", alignRight: false },
  { id: "member", label: "Coach", alignRight: false },
  { id: "program", label: "Programme", alignRight: false },
  { id: "type", label: "Type", alignRight: false },
  { id: "createdBy", label: "Created By", alignRight: false },
  { id: "CreatedAt", label: "Created At", alignRight: false },
  { id: "status", label: "Status", alignRight: false },
];

const useStyles = makeStyles(() => ({
  loading: {
    marginLeft: "50%",
    marginTop: "20%",
  },
}));

export default function ConsultantList() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const classes = useStyles();
  const { setIsOpenFormsList, setSelectedFormsMember } = useContentSetting();
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState("asc");
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState("number");
  const [filterName, setFilterName] = useState("");
  const [appliedFilterName, setAppliedFilterName] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const { enqueueSnackbar } = useSnackbar();
  const [deleteDoc, setDeleteDoc] = useState("");
  const [openDelete, setOpenDelete] = useState(false);

  const {
    isLoading,
    isFetching,
    data: queryData,
    refetch: getcoachListing,
  } = useQuery({
    queryKey: ["coachListing", page, rowsPerPage, appliedFilterName],
    queryFn: async () => {
      let postData = {
        search_text: appliedFilterName,
      };
      try {
        const result = await coachListing(page, rowsPerPage, postData);
        if (result.code === 200) {
          const data = result.coach_list.map((consultant) => {
            return {
              ...consultant,
              id: consultant?._id,
              fullName: consultant?.first_name + " " + consultant?.last_name,
              name: consultant?.first_name,
              lastName: consultant?.last_name,
              status: consultant?.status,
              email: consultant?.email,
              profile: consultant?.image?.thumbnail_1,
              address: consultant?.address,
              type: consultant?.team_type ? consultant?.team_type : "N/A",
              memberCount: consultant?.member_count,
              selected_address: consultant?.address,
              biography: consultant?.biography,
              selected_city: consultant?.city,
              selected_contact_number: consultant?.contact_number,
              main_heading: consultant?.main_heading,
              selected_state: consultant?.state,
              selected_team_type: consultant?.team_type,
              selected_time_zone: consultant?.time_zone,
              contactNumber: consultant?.contact_number,
              refUser: consultant?.affiliate_info,
              created_at: dd_date_format(consultant.createdAt),
              createdBy: consultant.created_by
                ? consultant.created_by
                : "Admin",
            };
          });
          return {
            userList: data,
            totalCount: result.total_coach_count,
            totalPages: result.total_pages,
            coach_list: result.coach_list,
          };
        } else {
          enqueueSnackbar(result.message, { variant: "error" });
          throw new Error(result.message);
        }
      } catch (error) {
        throw error;
      }
    },
    onError: (error) => {
      enqueueSnackbar(error.message, { variant: "error" });
    },
  });

  const userList = queryData?.userList || [];
  const totalCount = queryData?.totalCount || 0;

  const LoginAsMember = async (value) => {
    const data = {
      email: value.email,
      user_type: "coach",
    };

    const result = await member_login_api(data);
    if (result.code === 200) {
      window.open(`${coachPortalUrl}login-by-admin/${result?.token}`);
    } else {
      enqueueSnackbar(result.message, { variant: "error" });
    }
  };

  const handleEdit = (value) => {
    navigate(`/coach/edit-coach/${value.id}`, {
      state: value,
    });
  };

  const handleChat = (value) => {
    navigate(`/chat/?chat_id=${value.id}`, {
      state: value,
    });
  };

  const handleFormList = (value) => {
    setSelectedFormsMember({
      ...value,
      _id: value?._id,
      profile_image: value?.image?.thumbnail_1,
    });
    setIsOpenFormsList(true);
  };

  const handleChangePassword = (value) => {
    navigate(`/coach/change-password/${value.id}`, {
      state: value,
    });
  };

  const removeCoachFromCache = (ids) => {
    queryClient.setQueriesData(
      { queryKey: ["coachListing"], exact: false },
      (old) => {
        if (!old || !old.userList) {
          return old;
        }

        const filteredList = old.userList.filter((coach) => {
          const coachId = coach?.id || coach?._id;
          return !ids.includes(coachId);
        });

        if (filteredList.length === old.userList.length) {
          return old;
        }

        const removedCount = old.userList.length - filteredList.length;
        return {
          ...old,
          userList: filteredList,
          totalCount: Math.max((old.totalCount || 0) - removedCount, 0),
        };
      },
    );
  };

  const handleAgreeDelete = (value) => {
    setDeleteDoc(value);
    setOpenDelete(true);
  };

  const handleDelete = async () => {
    setOpenDelete(false);
    const previousQueries = queryClient.getQueriesData({
      queryKey: ["coachListing"],
      exact: false,
    });
    removeCoachFromCache([deleteDoc.id]);

    const result = await DeleteCoachApi(deleteDoc.id);
    if (result.code === 200) {
      await queryClient.invalidateQueries({
        queryKey: ["coachListing"],
        exact: false,
      });
      enqueueSnackbar(result.message, { variant: "success" });
    } else {
      previousQueries.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
      enqueueSnackbar(result.message, { variant: "error" });
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const Capitalize = (str) => {
    return str?.charAt(0).toUpperCase() + str?.slice(1);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
  };

  const handleNavigate = () => {
    navigate(`/coach/add-coach`);
  };

  const handleSearch = () => {
    setPage(0);
    setAppliedFilterName(filterName);
  };

  const MENU_OPTIONS1 = [
    {
      label: "Edit",
      icon: "akar-icons:edit",
      handleClick: handleEdit,
    },
    {
      label: "Chat",
      icon: "fluent:chat-28-filled",
      handleClick: handleChat,
    },
    {
      label: "Login As Coach",
      icon: "basil:login-solid",
      handleClick: LoginAsMember,
    },
    {
      label: "View Forms",
      icon: "fluent:form-48-filled",
      handleClick: handleFormList,
    },
    {
      label: "Delete",
      icon: "ant-design:delete-twotone",
      handleClick: handleAgreeDelete,
    },
    {
      label: "Change Password",
      icon: "ant-design:lock",
      handleClick: handleChangePassword,
    },
  ];
  const isUserNotFound = userList.length === 0;

  if (isLoading || (isFetching && !userList.length)) {
    return <CircularProgress className={classes.loading} color="primary" />;
  }

  return (
    <>
      <CustomConfirmation
        openDelete={openDelete}
        setOpenDelete={setOpenDelete}
        title={"Are you sure you want to delete ?"}
        handleAgree={handleDelete}
      />

      <div className="container-fluid">
        <div className="row">
          <div className="col-lg-8 col-sm-12">
            <h2>Coaches</h2>
          </div>
          <div className="col-lg-4 col-sm-12 text-end">
            <button onClick={handleNavigate} className="small-contained-button">
              Add Coach
            </button>
          </div>
        </div>
        <Card style={{ overflowX: "auto" }}>
          <div className="d-flex justify-content-between align-items-center">
            <TablePagination
              rowsPerPageOptions={[50, 100, 150]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
            <div className="d-flex align-items-center justify-content-end">
              <UserListToolbar
                numSelected={selected.length}
                filterName={filterName}
                onFilterName={handleFilterByName}
                onSearch={handleSearch}
              />
              <button
                onClick={handleSearch}
                className="small-contained-button me-3 "
              >
                Search
              </button>
            </div>
          </div>
          <TableContainer sx={{ minWidth: 800 }}>
            <Table>
              <UserListHead
                order={order}
                orderBy={orderBy}
                headLabel={TABLE_HEAD}
                rowCount={userList.length}
                numSelected={selected.length}
              />
              <TableBody>
                {userList.map((row, i) => {
                  const {
                    id,
                    fullName,
                    name,
                    status,
                    email,
                    profile,
                    program,
                    created_at,
                    createdBy,
                    coach_type,
                  } = row;
                  const isItemSelected = selected.indexOf(name) !== -1;

                  return (
                    <TableRow
                      hover
                      key={id}
                      tabIndex={-1}
                      role="checkbox"
                      selected={isItemSelected}
                      aria-checked={isItemSelected}
                    >
                      <TableCell>
                        <CustomPopoverSection menu={MENU_OPTIONS1} data={row} />
                      </TableCell>
                      <TableCell align="left">
                        {page * rowsPerPage + (i + 1)}
                      </TableCell>
                      <TableCell component="th" scope="row">
                        <div className="d-flex align-items-start gap-2">
                          <Avatar alt={name} src={s3baseUrl + profile} />
                          <div>
                            <Typography variant="subtitle2" noWrap>
                              {fullName}
                            </Typography>
                            <Typography
                              variant="caption"
                              noWrap
                              sx={{ color: "text.secondary" }}
                            >
                              {email ? email : "N/A"}
                            </Typography>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell
                        component="th"
                        scope="row"
                        className="program-exp"
                      >
                        {program && program.length > 0
                          ? program?.map((program) => {
                              return (
                                <>
                                  <p className="mb-1 modalIcon">
                                    {program?.title}
                                  </p>
                                </>
                              );
                            })
                          : "N/A"}
                      </TableCell>
                      <TableCell component="th" scope="row">
                        {Capitalize(coach_type ? coach_type : "N/A")}
                      </TableCell>
                      <TableCell className="text-capital">
                        {createdBy}
                      </TableCell>
                      <TableCell>{created_at}</TableCell>

                      <TableCell align="left">
                        <Label
                          variant="ghost"
                          color={status === false ? "error" : "success"}
                        >
                          {status === false ? "InActive" : "Active"}
                        </Label>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
              {isUserNotFound && (
                <TableBody>
                  <TableRow>
                    <TableCell align="center" colSpan={10} sx={{ py: 3 }}>
                      <SearchNotFound searchQuery={filterName} />
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[50, 100, 150]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
      </div>
    </>
  );
}
